import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { demands, pipes, type Demand, type SLA } from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatDate } from '@/shared/lib/format';
import { LinkComIcone } from '@/shared/ui/AcaoComIcone';
import { Alert } from '@/shared/ui/Alert';
import { Card } from '@/shared/ui/Card';
import { DataTable, type Column } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import type { IconName } from '@/shared/ui/Icon';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Pagination } from '@/shared/ui/Pagination';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import type { TipoDeAcao } from '@/shared/ui/tiposDeAcao';
import { buttonClassName } from '@/shared/ui/buttonStyles';

import { PartyToolbar } from '../shared/PartyToolbar';

/**
 * Fila de trabalho de uma etapa da esteira.
 *
 * Cada area trabalha por fila: quem analisa credito quer ver o que espera credito,
 * e nao a esteira inteira. A tela e a mesma para todas as etapas porque a pergunta
 * e a mesma; o que muda e a etapa e o verbo da acao.
 *
 * A fila nao decide nada. A decisao acontece na Operacao 360, onde estao o
 * contexto, o historico e a alcada; aqui se escolhe o que abrir. Duplicar a decisao
 * na fila faria a mesma regra existir em dois lugares.
 */

const limite = 25;

export function StageQueuePage({
  stage,
  titulo,
  descricao,
  icone,
  acao,
  vazio,
  esteira,
}: {
  stage: string;
  titulo: string;
  descricao: string;
  icone: IconName;
  acao: string;
  vazio: string;
  /**
   * Chave da esteira desta fila (P-EST-1). Com ela, a fila leva ao quadro da
   * esteira, onde a mesma carteira aparece em colunas.
   */
  esteira?: string;
}) {
  const { can } = useAuth();
  const [busca, setBusca] = useState('');
  const [offset, setOffset] = useState(0);

  const podeVerQuadro = esteira !== undefined && can('esteiras.esteira.view');
  const esteiras = useQuery({
    queryKey: ['pipes'],
    queryFn: ({ signal }) => pipes.list(signal),
    enabled: podeVerQuadro,
  });
  const doQuadro = esteiras.data?.find((p) => p.key === esteira);

  // A fila usa a visao de demanda, e nao a de operacao: ela ja traz responsavel,
  // prazo e pendencias abertas, que sao o que decide o que abrir primeiro.
  const consulta = useQuery({
    queryKey: ['demands', { stage, busca, offset }],
    queryFn: ({ signal }) =>
      demands.list({ stage, q: busca, limit: limite, offset }, signal),
  });

  const colunas: Column<Demand>[] = [
    {
      key: 'numero',
      header: 'Operacao',
      render: (demanda) => (
        <div className="min-w-0">
          <p className="font-medium text-ink-900">{demanda.number}</p>
          <p className="truncate text-2xs text-ink-500">{demanda.policyholder.label}</p>
        </div>
      ),
    },
    {
      key: 'produto',
      header: 'Produto e modalidade',
      hideBelow: 'lg',
      render: (demanda) => (
        <div className="min-w-0">
          <p className="truncate">{demanda.product.label}</p>
          <p className="truncate text-2xs text-ink-500">{demanda.modality.label}</p>
        </div>
      ),
    },
    {
      key: 'prazo',
      header: 'Prazo da etapa',
      align: 'right',
      render: (demanda) => <PrazoCelula sla={demanda.sla} />,
    },
    {
      key: 'responsavel',
      header: 'Responsavel',
      hideBelow: 'md',
      render: (demanda) => demanda.owner_label || 'Sem responsavel',
    },
    {
      key: 'pendencias',
      header: 'Pendencias',
      align: 'right',
      hideBelow: 'lg',
      render: (demanda) =>
        demanda.open_pendings > 0 ? (
          <span className="font-medium text-warning-700">{demanda.open_pendings}</span>
        ) : (
          <span className="text-ink-400">--</span>
        ),
    },
    {
      key: 'prioridade',
      header: 'Prioridade',
      hideBelow: 'lg',
      render: (demanda) => <StatusBadge kind="prioridade" value={demanda.priority} />,
    },
    {
      key: 'acao',
      header: 'Acao',
      align: 'right',
      render: (demanda) => (
        // A acao da fila abre a operacao. "Analisar" e a lupa sozinha, como o dono
        // pediu; os outros verbos levam o icone com o texto curto ao lado.
        <LinkComIcone
          to={`/operacoes/${demanda.id}`}
          rotulo={acao}
          texto={acao !== 'Analisar'}
          {...aparenciaDaAcaoDaFila(acao)}
        />
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={titulo}
        description={descricao}
        actions={
          doQuadro ? (
            <Link
              to={`/esteiras/${doQuadro.id}`}
              className={buttonClassName({ variant: 'secondary', size: 'md' })}
            >
              Ver no quadro da esteira
            </Link>
          ) : undefined
        }
      />

      {consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar a fila">
          {consulta.error.message}
        </Alert>
      ) : null}

      <Card>
        <PartyToolbar
          search={busca}
          onSearch={(valor) => {
            setBusca(valor);
            setOffset(0);
          }}
          placeholder="Buscar por numero, objeto, tomador ou corretora"
        />

        <DataTable
          columns={colunas}
          items={consulta.data?.items ?? []}
          keyOf={(demanda) => demanda.id}
          loading={consulta.isPending}
          empty={
            <EmptyState
              icon={icone}
              title={busca ? 'Nenhuma operacao encontrada' : 'Fila vazia'}
              description={busca ? 'Ajuste a busca para ver outras operacoes.' : vazio}
            />
          }
        />

        <Pagination
          total={consulta.data?.total ?? 0}
          limit={limite}
          offset={offset}
          onChange={setOffset}
        />
      </Card>
    </div>
  );
}

/** O icone do verbo da fila: a lupa de analisar, as moedas de cotar, o carimbo de emitir. */
function aparenciaDaAcaoDaFila(
  acao: string,
): { acao: TipoDeAcao } | { icone: IconName } {
  if (acao === 'Cotar') return { icone: 'coins' };
  if (acao === 'Emitir') return { icone: 'stamp' };
  return { acao: 'analisar' };
}

/**
 * Prazo da etapa, com o atraso dito em palavras.
 *
 * O prazo vencido e o motivo de a fila existir, e por isso aparece em destaque em
 * vez de exigir que a pessoa compare datas de cabeca.
 */
function PrazoCelula({ sla }: { sla: SLA }) {
  if (!sla.has_target || !sla.due_at) {
    return <span className="text-2xs text-ink-500">Sem prazo</span>;
  }

  return (
    <div className="flex flex-col items-end">
      <span
        className={
          sla.overdue
            ? 'font-medium text-danger-700'
            : sla.days_left <= 1
              ? 'font-medium text-warning-700'
              : 'text-ink-700'
        }
      >
        {sla.overdue
          ? `vencido ha ${Math.abs(sla.days_left)} d`
          : sla.days_left === 0
            ? 'vence hoje'
            : `${sla.days_left} d`}
      </span>
      <span className="text-2xs text-ink-500">{formatDate(sla.due_at)}</span>
    </div>
  );
}

export function CreditQueuePage() {
  return (
    <StageQueuePage
      stage="aguardando_credito"
      titulo="Analise de credito"
      descricao="Operacoes aguardando decisao de credito."
      icone="coins"
      acao="Analisar"
      vazio="Nenhuma operacao aguarda credito. Assim que uma chegar nesta etapa, ela aparece aqui."
      esteira="credito"
    />
  );
}

export function UnderwritingQueuePage() {
  return (
    <StageQueuePage
      stage="em_subscricao"
      titulo="Subscricao"
      descricao="Operacoes em analise de risco."
      icone="shield"
      acao="Analisar"
      vazio="Nenhuma operacao em subscricao. O credito aprovado encaminha para ca."
      esteira="subscricao"
    />
  );
}

export function LegalQueuePage() {
  return (
    <StageQueuePage
      stage="em_juridico"
      titulo="Juridico"
      descricao="Operacoes aguardando parecer juridico."
      icone="gavel"
      acao="Analisar"
      vazio="Nenhuma operacao no juridico. A subscricao aprovada encaminha para ca."
    />
  );
}

export function CommercialQueuePage() {
  return (
    <StageQueuePage
      stage="aguardando_comercial"
      titulo="Comercial e cotacoes"
      descricao="Operacoes aguardando confirmacao de premio e comissao."
      icone="target"
      acao="Cotar"
      vazio="Nenhuma operacao aguardando comercial."
    />
  );
}

export function IssuanceQueuePage() {
  return (
    <StageQueuePage
      stage="em_emissao"
      titulo="Emissao"
      descricao="Operacoes prontas para emitir a garantia junto a contraparte."
      icone="send"
      acao="Emitir"
      vazio="Nenhuma operacao pronta para emissao. A coleta de assinatura concluida encaminha para ca."
    />
  );
}
